import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import treeAwakeningImg from '@/assets/tree-awakening.png';

const EASE = [0.22, 0.61, 0.36, 1] as const;

const copy = {
  en: { rooted: 'Your words have taken root.', cta: 'Return to Your Tree' },
  es: { rooted: 'Tus palabras han echado raíces.', cta: 'Volver a tu Árbol' },
  pt: { rooted: 'Suas palavras criaram raízes.', cta: 'Voltar à sua Árvore' },
  fr: { rooted: 'Vos mots ont pris racine.', cta: 'Retourner à votre Arbre' },
  ar: { rooted: 'كلماتك قد ترسخت.', cta: 'العودة إلى شجرتك' },
  ja: { rooted: 'あなたの言葉が根を張りました。', cta: 'ツリーに戻る' },
  ko: { rooted: '당신의 말이 뿌리를 내렸습니다.', cta: '나무로 돌아가기' },
  id: { rooted: 'Kata-katamu telah berakar.', cta: 'Kembali ke Pohon Anda' },

  hi: { rooted: 'आपके शब्दों ने जड़ पकड़ ली है।', cta: 'अपने ट्री पर लौटें' },
};

interface TreeAwakeningProps {
  open: boolean;
  onComplete: () => void;
  recipientName?: string;
}

export function TreeAwakening({ open, onComplete }: TreeAwakeningProps) {
  const { language } = useLanguage();
  const t = lc(copy, language);
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    if (!open) { setPhase(0); return; }
    const timers = [
      setTimeout(() => setPhase(1), 600),
      setTimeout(() => setPhase(2), 2000),
      setTimeout(() => setPhase(3), 4200),
    ];
    return () => timers.forEach(clearTimeout);
  }, [open]);

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 1, ease: EASE as any }}
        className="fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden"
        style={{
          background: 'radial-gradient(ellipse at 50% 45%, #0a2218 0%, #051510 35%, #030d07 60%, #010503 100%)',
        }}
      >
        {/* Ambient fireflies */}
        {Array.from({ length: 18 }).map((_, i) => (
          <motion.div
            key={`fly-${i}`}
            className="absolute rounded-full pointer-events-none"
            style={{
              width: 2 + (i % 3),
              height: 2 + (i % 3),
              left: `${5 + (i * 4.7) % 90}%`,
              top: `${8 + (i * 5.3) % 84}%`,
              background: i % 3 === 0
                ? 'radial-gradient(circle, rgba(251,191,36,0.6), transparent)'
                : 'radial-gradient(circle, rgba(120,200,100,0.4), transparent)',
            }}
            animate={{
              y: [0, -(8 + i * 1.5), 0],
              x: [0, i % 2 ? 8 : -8, 0],
              opacity: [0, 0.5, 0],
            }}
            transition={{
              duration: 3 + (i % 4) * 0.8,
              delay: i * 0.3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        ))}

        {/* Phase 1: Text */}
        {phase >= 1 && (
          <motion.div
            className="z-20 text-center px-8 absolute"
            style={{ top: '8%' }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.4, ease: EASE as any }}
          >
            <p
              className="text-3xl md:text-5xl font-heading font-bold"
              style={{
                color: 'rgba(230,240,220,0.95)',
                textShadow: '0 0 60px rgba(120,200,100,0.4), 0 2px 20px rgba(0,0,0,0.5)',
              }}
            >
              {t.rooted}
            </p>
          </motion.div>
        )}

        {/* Phase 2: Tree — full-bleed, seamless */}
        {phase >= 2 && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 2.5, ease: EASE as any }}
          >
            {/* Breathing root glow — lower area */}
            <motion.div
              className="absolute pointer-events-none"
              style={{
                bottom: '8%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '70vw',
                maxWidth: 800,
                height: '30vh',
                background: 'radial-gradient(ellipse at 50% 30%, rgba(251,191,36,0.18) 0%, rgba(251,191,36,0.06) 40%, transparent 70%)',
              }}
              animate={{
                opacity: [0.4, 0.8, 0.4],
                scale: [1, 1.08, 1],
              }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            />

            {/* Breathing trunk glow — center */}
            <motion.div
              className="absolute pointer-events-none"
              style={{
                top: '30%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 200,
                height: 300,
                background: 'radial-gradient(ellipse at 50% 50%, rgba(251,191,36,0.12) 0%, transparent 60%)',
              }}
              animate={{
                opacity: [0.3, 0.7, 0.3],
                scale: [1, 1.15, 1],
              }}
              transition={{ duration: 2.5, delay: 0.5, repeat: Infinity, ease: 'easeInOut' }}
            />

            {/* The tree image — large, edge-faded, seamless */}
            <div
              className="relative"
              style={{
                width: '90vmin',
                maxWidth: 700,
                aspectRatio: '1',
              }}
            >
              <img
                src={treeAwakeningImg}
                alt="Living Tree"
                className="w-full h-full object-contain"
                style={{
                  maskImage: 'radial-gradient(ellipse 48% 48% at 50% 48%, black 60%, transparent 100%)',
                  WebkitMaskImage: 'radial-gradient(ellipse 48% 48% at 50% 48%, black 60%, transparent 100%)',
                }}
              />

              {/* Overlay breathing golden shimmer on the tree */}
              <motion.div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'radial-gradient(ellipse at 50% 55%, rgba(251,191,36,0.08) 0%, transparent 50%)',
                  mixBlendMode: 'screen',
                }}
                animate={{
                  opacity: [0.2, 0.5, 0.2],
                }}
                transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
              />
            </div>

            {/* Extra root glow pulses — left & right */}
            {[
              { left: '20%', bottom: '22%', delay: 0 },
              { left: '75%', bottom: '22%', delay: 0.8 },
              { left: '30%', bottom: '18%', delay: 1.4 },
              { left: '65%', bottom: '18%', delay: 0.4 },
            ].map((p, i) => (
              <motion.div
                key={`rg-${i}`}
                className="absolute rounded-full pointer-events-none"
                style={{
                  left: p.left,
                  bottom: p.bottom,
                  width: 80,
                  height: 40,
                  background: 'radial-gradient(ellipse, rgba(251,191,36,0.15) 0%, transparent 70%)',
                }}
                animate={{
                  opacity: [0, 0.6, 0],
                  scale: [0.8, 1.3, 0.8],
                }}
                transition={{
                  duration: 2.5,
                  delay: p.delay,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />
            ))}
          </motion.div>
        )}

        {/* Phase 3: CTA */}
        {phase >= 3 && (
          <motion.div
            className="absolute z-20"
            style={{ bottom: '8%' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE as any }}
          >
            <Button
              onClick={onComplete}
              variant="outline"
              size="lg"
              className="border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10 hover:text-emerald-100 bg-transparent backdrop-blur-sm text-base px-8 py-3"
              style={{
                boxShadow: '0 0 30px rgba(16,185,129,0.15), inset 0 0 20px rgba(16,185,129,0.05)',
              }}
            >
              {t.cta}
            </Button>
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
